import dayjs from 'dayjs'

import { getUserSetting, setUserSetting } from '@/common/utils/userSettings'
import { Filder } from '@/project/documents/types'
import store from '@/store/'
import { GlobalState } from '@/store/modules/global/state'

import { WORD_EXTENSIONS } from '../utils/office'

type PendoEvent =
  | 'create_checklist'
  | 'checklist_import_started'
  | 'checklist_import_completed'
  | 'checklist_import_uploaded'
  | 'create_home_folder'
  | 'duplicate_M365_document'
  | 'open_M365_document'
  | 'request_document_ai_summary'
  | 'swap_success'
  | 'redact_started'
  | 'redaction_mode_used'
  | 'redact_by_keywords'
  | 'redact_by_zone'
  | 'redact_cancelled'
  | 'redact_saved'
  | 'redaction_started'
  | 'document_uploaded'
  | 'document_previewed'
  | 'document_downloaded'
  | 'project_closed'
  | 'project_bible'
  | 'user_added'
  | 'mass_user_added'
  | 'create_dr'
  | 'create_tm'
  | 'question_created'
  | 'mass_question_created'
  | 'question_answered'
  | 'question_closed'
  | 'question_deleted'
  | 'new_version_created'
  | 'ask_doc_new_conversation'
  | 'ask_doc_prompt_sent'
  | 'ask_doc_sources_concerned'

declare global {
  // We don't manage that type and Pendo doesn't provide one. A global var is what types globalThis.pendo
  // eslint-disable-next-line no-var
  var pendo: {
    track (eventName: PendoEvent, payload?: Record<PropertyKey, unknown>): void
    // Does not report whether the guide was displayed: confirm it through getActiveGuide()
    showGuideById (guideId: string): void
    getActiveGuide (): { guide: { id: string } } | null
  }
}

/**
 * Whether the pendo agent is loaded. Any call to pendo must be guarded by this check.
 */
function isPendoLoaded () {
  return (store.state as unknown as ObjectWith<{ global: GlobalState }>).global.isPendoLoaded
}

/**
 * A function that wraps the pendo agent track method safely.
 * Can also be used to add more information to every tracked event.
 * @param {string} eventName Name of the event to track
 * @param {object} payload Object containing properties to send to Pendo
 */
function track (eventName:PendoEvent, payload?: Record<PropertyKey, unknown>) {
  if (isPendoLoaded()) {
    globalThis.pendo.track(eventName, payload)
  }
}

/**
 * Determines the PDF content type based on the file properties for Pendo tracking purposes.
 * @param file {Filder} The file for which to determine the PDF content type
 * @returns {String|null} The PDF content type ('text', 'scanned', 'converted' or null if not a PDF)
 */
function getPdfContentType (file: Filder) {
  if (file.ext === 'pdf') {
    return file.pdfHasText ? 'text' : 'scanned'
  } else {
    if (file.ext && WORD_EXTENSIONS.includes(file.ext) && file.pdfHasText) {
      return 'converted'
    }
    return null
  }
}


/**
 * User role constants for Pendo tracking purposes.
 */
const USER_ROLE = {
  PROJECT_MANAGER: 'project_manager',
  GUEST: 'guest',
} as const

const IMPORT_SOURCES = {
  EXCEL: 'excel',
} as const

type DocumentExtensionSource = {
  ext?: string | null
  name?: string | null
  basename?: string | null
}

/**
 * Derives the unique, lowercased file extensions from a list of selected documents
 * for Pendo tracking. Uses an explicit `ext` when available, otherwise parses the
 * file name/basename.
 * @param documents {DocumentExtensionSource[]} The selected documents
 * @returns {string[]} Unique extensions, e.g. ['pdf', 'docx']
 */
function docExtensions (documents?: DocumentExtensionSource[] | null): string[] {
  const extensions = (documents || []).map((document) => {
    if (document.ext) return String(document.ext).toLowerCase()
    const name = document.name || document.basename || ''
    return name.includes('.') ? (name.split('.').pop() || '').toLowerCase() : ''
  }).filter(Boolean)

  return [...new Set(extensions)]
}

/**
 * Surveys (NPS / CSAT) are API-activated Pendo guides: Pendo only handles each guide's own
 * 180-day recurrence, the per-module cooldown and the "no overlap" rule are enforced here.
 * See 3107193 for details
 */
enum SurveyModule {
  DATA_ROOM = 'data-room',
  LEGAL_PROJECT = 'legal-project',
}

const SURVEY_GUIDE_IDS = {
  [SurveyModule.DATA_ROOM]: {
    NPS: 'vVKtiaZNQLRapo4G-gagTrtSjFc',
    CSAT_CREATE_QUESTION: '8KPSEsOzTvC_V48LO3x-Emj7uok',
    CSAT_SHARE_DOCUMENT: 'eU0X6GYHMKrUEWA6XeHMX43aYTM',
  },
  [SurveyModule.LEGAL_PROJECT]: {
    NPS: 'QowON7z_rAm-BCUnzt9Mo189ikM',
    CSAT_CREATE_TASK: 'bbNEWzNYn53yh4nk-y6MX4lSVdg',
    CSAT_LAUNCH_CLOSING: 'USTonKAUxbJ1-UyAkMcNw-LiB3c',
  },
} as const

const SURVEY_MODULE_COOLDOWN_DAYS = 30
const NPS_SURVEY_DELAY_MS = 30 * 1000
// Pendo may render the guide asynchronously after showGuideById
const SURVEY_DISPLAY_CHECK_DELAY_MS = 2000

// Blocks any other survey while a display is being confirmed, so two guides can't both start
let isSurveyDisplayPending = false

const LAST_SURVEY_SHOWN_AT_KEY_SUFFIX = 'last-pendo-survey-shown-at'

function getCurrentUserId () {
  return (store.state as unknown as ObjectWith<{ user: { profile?: { id?: number } } }>).user.profile?.id
}

function getLastSurveyShownAtKey (moduleKey: SurveyModule) {
  return `${moduleKey}-${LAST_SURVEY_SHOWN_AT_KEY_SUFFIX}`
}

function getSurveyModule (isDataRoom: boolean) {
  return isDataRoom ? SurveyModule.DATA_ROOM : SurveyModule.LEGAL_PROJECT
}

/**
 * @returns {string | null} ISO date of the last guide shown to the current user within the module
 */
function getLastSurveyShownAt (moduleKey: SurveyModule): string | null {
  const userId = getCurrentUserId()
  if (!userId) {
    return null
  }
  return getUserSetting(userId, getLastSurveyShownAtKey(moduleKey))
}

function setLastSurveyShownAt (moduleKey: SurveyModule) {
  const userId = getCurrentUserId()
  if (!userId) {
    return
  }
  setUserSetting(userId, getLastSurveyShownAtKey(moduleKey), dayjs().toISOString())
}

function canShowSurvey (moduleKey: SurveyModule) {
  if (!getCurrentUserId()) {
    return false
  }

  const lastSurveyShownAt = getLastSurveyShownAt(moduleKey)
  if (!lastSurveyShownAt || !dayjs(lastSurveyShownAt).isValid()) {
    return true
  }

  return !dayjs().isBefore(dayjs(lastSurveyShownAt).add(SURVEY_MODULE_COOLDOWN_DAYS, 'day'))
}

function isGuideDisplayed (guideId: string) {
  return globalThis.pendo.getActiveGuide()?.guide.id === guideId
}

function waitForGuideDisplay (guideId: string): Promise<boolean> {
  return new Promise(resolve => {
    if (isGuideDisplayed(guideId)) {
      resolve(true)
      return
    }
    setTimeout(() => resolve(isGuideDisplayed(guideId)), SURVEY_DISPLAY_CHECK_DELAY_MS)
  })
}

/**
 * Displays the Pendo guide if the module is out of its cooldown and no other guide is on screen.
 * The cooldown only starts once getActiveGuide() confirms Pendo displayed the guide.
 * @returns {Promise<boolean>} whether the guide was displayed
 */
async function triggerSurvey (guideId: string, moduleKey: SurveyModule) {
  if (!isPendoLoaded() || isSurveyDisplayPending || !canShowSurvey(moduleKey) || globalThis.pendo.getActiveGuide()) {
    return false
  }

  isSurveyDisplayPending = true
  try {
    globalThis.pendo.showGuideById(guideId)
    const isShown = await waitForGuideDisplay(guideId)
    if (isShown) {
      setLastSurveyShownAt(moduleKey)
    }

    return isShown
  } finally {
    isSurveyDisplayPending = false
  }
}

export {
  isPendoLoaded,
  track,
  getPdfContentType,
  docExtensions,
  USER_ROLE,
  IMPORT_SOURCES,
  SurveyModule,
  SURVEY_GUIDE_IDS,
  SURVEY_MODULE_COOLDOWN_DAYS,
  NPS_SURVEY_DELAY_MS,
  getSurveyModule,
  getLastSurveyShownAt,
  setLastSurveyShownAt,
  canShowSurvey,
  triggerSurvey,
}
